// @flow
import {useEffect, useState} from 'react';
import GoogleMapsDrawing from '../map-drawing/maps/GoogleMaps';
import {useGoogleMapsApi} from '../../../utils/dom';
import {GoogleMapsGeocodeServices} from '../map-services/geocode-service/GoogleMaps';
import {GoogleMapsAutocompleteServices} from '../map-services/autocomplete-service/GoogleMaps';
import {OpenStreetMapsAutocompleteServices} from '../map-services/autocomplete-service/OpenStreetMaps';
import {HereAutocompleteServices} from '../map-services/autocomplete-service/Here';

import type {
  MapAutocompleteServices,
  MapGeocodeServices,
} from '../map-services/MapServices';
import type {MapDrawing} from '../map-drawing/MapDrawing';
import type {ShippingDetail} from '../../../entities/order';
import type {AddressGeo as Location} from '../../../entities';

export type MapType = 'GOOGLE' | 'OSM' | 'HERE';

export function useMapProvider(
  mapAutocompleteServicesType?: MapType,
  mapGeocodeServicesType?: MapType,
  mapDrawingType: MapType
): [?MapAutocompleteServices, ?MapGeocodeServices, ?MapDrawing] {
  const [
    mapAutocompleteServices,
    setMapAutocompleteServices,
  ] = useState<MapAutocompleteServices | null>(null);
  const [
    mapGeocodeServices,
    setMapGeocodeServices,
  ] = useState<MapGeocodeServices | null>(null);
  const [mapDrawing, setMapDrawing] = useState<MapDrawing | null>(null);

  const googleProvider = useGoogleMapsApi('useMapProvider');

  useEffect(() => {
    if (
      !mapGeocodeServices &&
      googleProvider &&
      mapGeocodeServicesType === 'GOOGLE'
    ) {
      setMapGeocodeServices(new GoogleMapsGeocodeServices(googleProvider));
    }
  }, [googleProvider, mapGeocodeServices, mapGeocodeServicesType]);
  //TODO: when  mapGeocodeServicesType === 'HERE' | 'OSM' setMapGeocodeServices is required.

  useEffect(() => {
    if (
      !mapAutocompleteServices &&
      googleProvider &&
      mapAutocompleteServicesType === 'HERE'
    ) {
      setMapAutocompleteServices(new HereAutocompleteServices());
    }
  }, [googleProvider, mapAutocompleteServices, mapAutocompleteServicesType]);

  if (
    googleProvider &&
    mapAutocompleteServicesType === 'GOOGLE' &&
    !mapAutocompleteServices
  )
    setMapAutocompleteServices(
      new GoogleMapsAutocompleteServices(googleProvider)
    );

  if (
    googleProvider &&
    mapAutocompleteServicesType === 'OSM' &&
    !mapAutocompleteServices
  ) {
    setMapAutocompleteServices(new OpenStreetMapsAutocompleteServices());
  }

  useEffect(() => {
    if (!mapDrawing && googleProvider && mapDrawingType === 'GOOGLE') {
      setMapDrawing(new GoogleMapsDrawing(googleProvider));
    }
  }, [googleProvider, mapDrawing, mapDrawingType]);

  // if (mapDrawingType === 'OSM' && !mapDrawing) setMapDrawing(new OSMDrawing());

  return [mapAutocompleteServices, mapGeocodeServices, mapDrawing];
}

export type CustomAddress = {
  description?: string,
  displayName?: string,
  address1: string,
  address2?: string,
  admin1?: string,
  admin2?: string,
  admin3?: string,
};
export function getAddressFromShippingDetail(
  shippingDetail: ShippingDetail
): CustomAddress {
  if (shippingDetail.shippingAddressType === 'GEO') {
    const {shippingAddressGeo} = shippingDetail;
    if (shippingAddressGeo)
      return {
        address1: shippingAddressGeo.address1,
        address2: shippingAddressGeo.address2
          ? shippingAddressGeo.address2
          : '',
      };
  } else if (shippingDetail.shippingAddressType === 'RAW') {
    const {shippingAddressRaw} = shippingDetail;
    if (shippingAddressRaw)
      return {
        displayName: shippingAddressRaw.displayName
          ? shippingAddressRaw.displayName
          : '',
        description: shippingAddressRaw.description
          ? shippingAddressRaw.description
          : '',
        address1: shippingAddressRaw.address1
          ? shippingAddressRaw.address1
          : '',
        address2: shippingAddressRaw.address2
          ? shippingAddressRaw.address2
          : '',
        admin1: shippingAddressRaw.admin1 ? shippingAddressRaw.admin1 : '',
        admin2: shippingAddressRaw.admin2 ? shippingAddressRaw.admin2 : '',
        admin3: shippingAddressRaw.admin3 ? shippingAddressRaw.admin3 : '',
      };
  } else {
    const {shippingAddressAdmin} = shippingDetail;
    if (shippingAddressAdmin)
      return {
        displayName: shippingAddressAdmin.displayName,
        description: shippingAddressAdmin.description,
        address1: shippingAddressAdmin.address1,
        address2: shippingAddressAdmin.address2,
        admin1: shippingAddressAdmin.admin1.admin1Name,
        admin2: shippingAddressAdmin.admin2.admin2Name,

        admin3: shippingAddressAdmin.admin3
          ? shippingAddressAdmin.admin3.admin3Name
          : '',
      };
  }
  return {};
}

export type Provider = 'GOOGLE_MAPS' | 'OPEN_STREET_MAP';

export type LocationGeo = {
  placeId: string,
  location: {lat: number, lng: number},
  addressComponents: any[],
};

export function getGoogleMapsData(data: LocationGeo): Location | null {
  if (data) {
    let newValue: Location = {
      placeId: data.placeId,
      address1: '',
      zipCode: '',
      country: '',
      admin1: '',
      admin2: '',
      admin3: '',
      admin4: '',
      admin5: '',
      lat: String(data.location.lat),
      lng: String(data.location.lng),
    };

    data.addressComponents.forEach(component => {
      if (component.types.indexOf('postal_code') !== -1)
        newValue.zipCode = component.long_name;
      if (component.types.indexOf('country') !== -1)
        newValue.country = component.short_name;
      if (component.types.indexOf('administrative_area_level_1') !== -1)
        newValue.admin1 = component.long_name;
      if (component.types.indexOf('administrative_area_level_2') !== -1)
        newValue.admin2 = component.long_name;
      if (component.types.indexOf('locality') !== -1)
        newValue.admin3 = component.long_name;
      if (component.types.indexOf('route'))
        newValue.admin4 = component.long_name;
      if (component.types.indexOf('street_number') !== -1)
        newValue.admin5 = component.long_name;
    });
    return newValue;
  }

  return null;
}

export function formatAddressString(
  admin1: string,
  admin2?: string,
  admin3?: string
): string {
  let fullAddress = `${admin1}`;
  if (admin2) {
    fullAddress = `${fullAddress}, ${admin2}`;
    if (admin3) fullAddress = `${fullAddress}, ${admin3}`;
  }

  return fullAddress;
}
